import { create } from "zustand";
import type { Conversation, Message, PresenceKind, Reaction, User } from "@swift/shared";
import type { OutboxItem } from "./outbox";

type ConnectionStatus = "disconnected" | "connecting" | "connected";

interface State {
  me: User | null;
  users: Record<number, User>;
  conversations: Record<number, Conversation>;
  conversationOrder: number[];
  messagesByConv: Record<number, Message[]>;
  hasMoreByConv: Record<number, boolean>;
  typingByConv: Record<number, number[]>;
  activeConversationId: number | null;
  connection: ConnectionStatus;
  windowFocused: boolean;
  lightbox: { url: string; filename: string } | null;
  settingsOpen: boolean;
  mobileSidebarOpen: boolean;
  // Inkomende "Deel met Swift"-actie (Android): tekst en/of bestanden die
  // wachten tot de gebruiker een kanaal/gesprek kiest. null = geen.
  pendingShare: { text: string | null; files: File[] } | null;
  // Actief antwoord: welk bericht is de gebruiker aan het quoten in de
  // Composer? Per conversatie zodat switchen tussen kanalen niet de
  // verkeerde quote bewaart. null = geen reply actief in dat gesprek.
  replyTargets: Record<number, { messageId: number; senderName: string; preview: string } | null>;
  // Berichten in de offline-wachtrij (IndexedDB persistente queue).
  // Spiegel van listOutbox() — hier zodat de banner reactief kan
  // updaten zonder elk Composer.send opnieuw te queryen.
  outboxItems: OutboxItem[];
  // Gepinde berichten per kanaal. Wordt lazy gevuld bij eerste opening
  // van de pin-balk en bijgewerkt via socket-events pin:added/removed.
  pinnedByConv: Record<number, Message[]>;
  // Mention-lade-overlay open? Inhoud wordt lazy gefetcht door
  // MentionsDrawer zelf.
  mentionsOpen: boolean;

  setMe(user: User | null): void;
  setUsers(users: User[]): void;
  upsertUser(user: User): void;
  setConversations(list: Conversation[]): void;
  upsertConversation(conv: Conversation): void;
  setLastRead(conversationId: number, lastReadId: number): void;
  setMessages(convId: number, messages: Message[], hasMore: boolean): void;
  prependMessages(convId: number, messages: Message[], hasMore: boolean): void;
  appendMessage(message: Message): void;
  replaceMessage(message: Message): void;
  markMessageDeleted(conversationId: number, messageId: number): void;
  applyReactionAdded(reaction: Reaction): void;
  applyReactionRemoved(messageId: number, userId: number, emoji: string): void;
  setPresence(userId: number, kind: PresenceKind, statusText: string | null): void;
  setTyping(convId: number, userId: number, on: boolean): void;
  setActiveConversation(id: number | null): void;
  setConnection(s: ConnectionStatus): void;
  setWindowFocused(b: boolean): void;
  openLightbox(url: string, filename: string): void;
  closeLightbox(): void;
  openSettings(): void;
  closeSettings(): void;
  removeConversation(conversationId: number): void;
  setMobileSidebarOpen(open: boolean): void;
  setPendingShare(share: { text: string | null; files: File[] } | null): void;
  setReplyTarget(
    conversationId: number,
    target: { messageId: number; senderName: string; preview: string } | null,
  ): void;
  setOutboxItems(items: OutboxItem[]): void;
  setPinned(conversationId: number, messages: Message[]): void;
  upsertPinned(conversationId: number, message: Message): void;
  removePinned(conversationId: number, messageId: number): void;
  openMentions(): void;
  closeMentions(): void;
}

export const useStore = create<State>((set) => ({
  me: null,
  users: {},
  conversations: {},
  conversationOrder: [],
  messagesByConv: {},
  hasMoreByConv: {},
  typingByConv: {},
  activeConversationId: null,
  connection: "disconnected",
  windowFocused: true,
  lightbox: null,
  settingsOpen: false,
  mobileSidebarOpen: false,
  pendingShare: null,
  replyTargets: {},
  outboxItems: [],
  pinnedByConv: {},
  mentionsOpen: false,

  setMe: (me) => set({ me }),
  setUsers: (users) =>
    set(() => ({
      users: Object.fromEntries(users.map((u) => [u.id, u])),
    })),
  upsertUser: (u) =>
    set((s) => ({ users: { ...s.users, [u.id]: { ...s.users[u.id], ...u } } })),
  setConversations: (list) =>
    set(() => ({
      conversations: Object.fromEntries(list.map((c) => [c.id, c])),
      conversationOrder: list.map((c) => c.id),
    })),
  upsertConversation: (conv) =>
    set((s) => {
      const order = s.conversationOrder.includes(conv.id)
        ? s.conversationOrder
        : [conv.id, ...s.conversationOrder];
      return {
        conversations: { ...s.conversations, [conv.id]: conv },
        conversationOrder: order,
      };
    }),
  setLastRead: (conversationId, lastReadId) =>
    set((s) => {
      const conv = s.conversations[conversationId];
      if (!conv) return s;
      // Alleen omhoog bewegen — een latere read-advance mag nooit een
      // eerder al gelezen pointer omlaag duwen.
      const current = conv.last_read_message_id ?? 0;
      if (lastReadId <= current) return s;
      // Server-teller herrekenen vanuit wat er lokaal geladen is: alles
      // t/m lastReadId is nu gelezen; wat daarboven nog in het geheugen
      // zit (race: bericht kwam binnen tijdens het read-advancen) blijft
      // als ongelezen staan.
      const loaded = s.messagesByConv[conversationId] ?? [];
      const stillUnread = loaded.filter(
        (m) => m.id > lastReadId && m.sender_id !== s.me?.id && !m.deleted_at,
      ).length;
      return {
        conversations: {
          ...s.conversations,
          [conversationId]: {
            ...conv,
            last_read_message_id: lastReadId,
            unread_count: stillUnread,
          },
        },
      };
    }),
  setMessages: (convId, messages, hasMore) =>
    set((s) => ({
      messagesByConv: { ...s.messagesByConv, [convId]: messages },
      hasMoreByConv: { ...s.hasMoreByConv, [convId]: hasMore },
    })),
  prependMessages: (convId, messages, hasMore) =>
    set((s) => {
      const existing = s.messagesByConv[convId] ?? [];
      return {
        messagesByConv: { ...s.messagesByConv, [convId]: [...messages, ...existing] },
        hasMoreByConv: { ...s.hasMoreByConv, [convId]: hasMore },
      };
    }),
  appendMessage: (message) =>
    set((s) => {
      const list = s.messagesByConv[message.conversation_id] ?? [];
      if (list.some((m) => m.id === message.id)) return s;
      const order = [
        message.conversation_id,
        ...s.conversationOrder.filter((id) => id !== message.conversation_id),
      ];
      const existing = s.conversations[message.conversation_id];
      // Ongelezen-teller live bijhouden: server gaf de startstand mee bij
      // listConversations, hier +1 per binnenkomend andermans bericht.
      // Eigen berichten tellen niet; read-advance (setLastRead) reset.
      const unreadBump =
        message.sender_id !== s.me?.id ? (existing?.unread_count ?? 0) + 1 : existing?.unread_count;
      return {
        messagesByConv: {
          ...s.messagesByConv,
          [message.conversation_id]: [...list, message],
        },
        conversationOrder: order,
        conversations: {
          ...s.conversations,
          [message.conversation_id]: {
            ...(existing ?? {
              id: message.conversation_id,
              kind: "dm",
              name: null,
              member_ids: [],
              last_read_message_id: null,
              last_message_at: null,
            }),
            last_message_at: message.created_at,
            unread_count: unreadBump,
          } as Conversation,
        },
      };
    }),
  replaceMessage: (message) =>
    set((s) => {
      const list = s.messagesByConv[message.conversation_id];
      if (!list) return s;
      return {
        messagesByConv: {
          ...s.messagesByConv,
          [message.conversation_id]: list.map((m) => (m.id === message.id ? message : m)),
        },
      };
    }),
  markMessageDeleted: (conversationId, messageId) =>
    set((s) => {
      const list = s.messagesByConv[conversationId];
      if (!list) return s;
      const now = new Date().toISOString();
      return {
        messagesByConv: {
          ...s.messagesByConv,
          [conversationId]: list.map((m) =>
            m.id === messageId
              ? { ...m, body: "", deleted_at: now, attachments: [], reactions: [] }
              : m,
          ),
        },
      };
    }),
  applyReactionAdded: (reaction) =>
    set((s) => {
      const convId = findConversationOfMessage(s.messagesByConv, reaction.message_id);
      if (convId === null) return s;
      const list = s.messagesByConv[convId]!;
      return {
        messagesByConv: {
          ...s.messagesByConv,
          [convId]: list.map((m) =>
            m.id === reaction.message_id
              ? {
                  ...m,
                  reactions: m.reactions.some(
                    (r) =>
                      r.user_id === reaction.user_id &&
                      r.emoji === reaction.emoji,
                  )
                    ? m.reactions
                    : [...m.reactions, reaction],
                }
              : m,
          ),
        },
      };
    }),
  applyReactionRemoved: (messageId, userId, emoji) =>
    set((s) => {
      const convId = findConversationOfMessage(s.messagesByConv, messageId);
      if (convId === null) return s;
      const list = s.messagesByConv[convId]!;
      return {
        messagesByConv: {
          ...s.messagesByConv,
          [convId]: list.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  reactions: m.reactions.filter(
                    (r) => !(r.user_id === userId && r.emoji === emoji),
                  ),
                }
              : m,
          ),
        },
      };
    }),
  setPresence: (userId, kind, statusText) =>
    set((s) => {
      const existing = s.users[userId];
      if (!existing) return s;
      return {
        users: {
          ...s.users,
          [userId]: { ...existing, status_kind: kind, status_text: statusText },
        },
      };
    }),
  setTyping: (convId, userId, on) =>
    set((s) => {
      const current = s.typingByConv[convId] ?? [];
      const without = current.filter((id) => id !== userId);
      return {
        typingByConv: {
          ...s.typingByConv,
          [convId]: on ? [...without, userId] : without,
        },
      };
    }),
  setActiveConversation: (id) =>
    set({ activeConversationId: id, mobileSidebarOpen: false }),
  setConnection: (connection) => set({ connection }),
  setWindowFocused: (windowFocused) => set({ windowFocused }),
  openLightbox: (url, filename) => set({ lightbox: { url, filename } }),
  closeLightbox: () => set({ lightbox: null }),
  openSettings: () => set({ settingsOpen: true }),
  closeSettings: () => set({ settingsOpen: false }),
  setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),
  setPendingShare: (pendingShare) => set({ pendingShare }),
  setReplyTarget: (conversationId, target) =>
    set((s) => ({
      replyTargets: { ...s.replyTargets, [conversationId]: target },
    })),
  setOutboxItems: (outboxItems) => set({ outboxItems }),
  setPinned: (conversationId, messages) =>
    set((s) => ({
      pinnedByConv: { ...s.pinnedByConv, [conversationId]: messages },
    })),
  upsertPinned: (conversationId, message) =>
    set((s) => {
      const current = s.pinnedByConv[conversationId] ?? [];
      const without = current.filter((m) => m.id !== message.id);
      // Nieuwste pin bovenaan — matcht de server-sortering (pinned_at desc).
      return {
        pinnedByConv: {
          ...s.pinnedByConv,
          [conversationId]: [message, ...without],
        },
        // Tegelijk de pinned-vlag op 't bericht in de stream bijwerken,
        // anders ziet de gebruiker de pin-indicator pas na een refresh.
        messagesByConv: applyMessageInList(s.messagesByConv, message),
      };
    }),
  openMentions: () => set({ mentionsOpen: true }),
  closeMentions: () => set({ mentionsOpen: false }),
  removePinned: (conversationId, messageId) =>
    set((s) => {
      const current = s.pinnedByConv[conversationId] ?? [];
      const stream = s.messagesByConv[conversationId] ?? [];
      return {
        pinnedByConv: {
          ...s.pinnedByConv,
          [conversationId]: current.filter((m) => m.id !== messageId),
        },
        messagesByConv: {
          ...s.messagesByConv,
          [conversationId]: stream.map((m) =>
            m.id === messageId ? { ...m, pinned: false } : m,
          ),
        },
      };
    }),
  removeConversation: (conversationId) =>
    set((s) => {
      const { [conversationId]: _gone, ...restConvs } = s.conversations;
      void _gone;
      const { [conversationId]: _msgs, ...restMsgs } = s.messagesByConv;
      void _msgs;
      return {
        conversations: restConvs,
        conversationOrder: s.conversationOrder.filter((id) => id !== conversationId),
        messagesByConv: restMsgs,
        activeConversationId:
          s.activeConversationId === conversationId ? null : s.activeConversationId,
      };
    }),
}));

/** Vervangt 't bericht in messagesByConv door de nieuwe versie als 't
 *  al in de lijst zit; laat 'm anders ongewijzigd. Gebruikt om de
 *  pinned-vlag bij te werken zonder dat een stream-rerender nodig is. */
function applyMessageInList(
  byConv: Record<number, Message[]>,
  message: Message,
): Record<number, Message[]> {
  const list = byConv[message.conversation_id];
  if (!list) return byConv;
  if (!list.some((m) => m.id === message.id)) return byConv;
  return {
    ...byConv,
    [message.conversation_id]: list.map((m) =>
      m.id === message.id ? message : m,
    ),
  };
}

function findConversationOfMessage(
  byConv: Record<number, Message[]>,
  messageId: number,
): number | null {
  for (const [convId, list] of Object.entries(byConv)) {
    if (list.some((m) => m.id === messageId)) return Number(convId);
  }
  return null;
}
